import type { PresentationData } from '../../types/presentation'
import {
  StandardTitleSlide,
  StandardGridSlide,
  StandardContentSlide,
  FeatureCard,
  InfoBox,
  AgendaItem,
  BlockDivider,
} from '../../components/presentation'
import { IconSolid, PromptModal, ModalTriggerButton } from '../../components/presentation'
import React from 'react'
import entwicklerschulungPrompts from './entwicklerschulungPrompts'

/**
 * KI-Entwicklerschulung Tag 1
 * 
 * REFACTORED VERSION using standardized design system
 * All slides now use consistent layouts, spacing, and typography
 */

export const entwicklerschulungTag1: PresentationData = {
  metadata: {
    title: 'KI-Entwicklerschulung Tag 1',
    description: 'KI-Grundlagen & AI-Coding-Tools für Entwickler',
    author: 'KI-Experten & Entwicklungs-Consultants',
    date: 'Oktober 2025',
    tags: ['KI', 'AI-Tools', 'Entwicklung', 'Workshop'],
  },
  slides: [
    // ============================================
    // SLIDE 1: Title
    // ============================================
    {
      id: 1,
      component: () => (
        <StandardTitleSlide
          title="KI-Entwicklerschulung Tag 1"
          subtitle="KI-Grundlagen & AI-Coding-Tools für Entwickler"
          author="KI-Experten & Entwicklungs-Consultants"
          date="Oktober 2025"
          tags={['8 Stunden Training', 'Für alle Level', 'Hands-on']}
          background="primary"
          footer={
            <div className="flex flex-wrap justify-center gap-6 text-white pb-4">
              <div className="text-center">
                <IconSolid name="clock" size="lg" className="mx-auto mb-1" />
                <p className="font-medium text-xs">8 Stunden Training</p>
              </div>
              <div className="text-center">
                <IconSolid name="graduation-cap" size="lg" className="mx-auto mb-1" />
                <p className="font-medium text-xs">Alle Level</p>
              </div>
              <div className="text-center">
                <IconSolid name="laptop-code" size="lg" className="mx-auto mb-1" />
                <p className="font-medium text-xs">Hands-on</p>
              </div>
            </div>
          }
        />
      ),
      transition: 'fade',
    },

    // ============================================
    // SLIDE 2: Agenda
    // ============================================
    {
      id: 2,
      component: () => (
        <StandardGridSlide
          title="Tagesagenda - Tag 1"
          subtitle="Von KI-Grundlagen zu praktischen Coding-Tools"
          columns={2}
          background="secondary"
          items={[
            <AgendaItem
              icon={<IconSolid name="brain" />}
              title="Block 1: KI-Fundamentals"
              duration="09:00 - 10:45"
              items={[
                'KI-Landschaft verstehen (ML, DL, GenAI)',
                'Large Language Models Deep Dive',
                'Prompt Engineering Essentials',
              ]}
            />,
            <AgendaItem
              icon={<IconSolid name="tools" />}
              title="Block 2: AI-Coding-Tools"
              duration="10:45 - 12:30"
              items={[
                'Tool-Matrix & Vergleich',
                'GitHub Copilot, Cursor, Claude, Cline',
                'Live-Demos & Einsatzszenarien',
              ]}
            />,
            <AgendaItem
              icon={<IconSolid name="code" />}
              title="Block 3: Hands-on Copilot"
              duration="13:15 - 14:30"
              items={[
                'GitHub Copilot Setup & Konfiguration',
                'Core Workflows: Completion, Chat, TDD',
                'Workshop: E-Commerce Product API',
              ]}
            />,
            <AgendaItem
              icon={<IconSolid name="rocket" />}
              title="Block 4: Praxis & Security"
              duration="14:45 - 16:00"
              items={[
                'Workshop: Legacy Code Modernisierung',
                'Workshop: Test Coverage erhöhen',
                'Security Best Practices',
              ]}
            />,
            <AgendaItem
              icon={<IconSolid name="bullseye" />}
              title="Block 5: Context Engineering"
              duration="16:00 - 16:45"
              items={[
                'Systematische KI-Entwicklung',
                'Framework & Best Practices',
                'Praktische Übung',
              ]}
            />,
          ]}
          footer={
            <InfoBox variant="tip">
              <strong>Ziel:</strong> Nach diesem Tag können Sie AI-Coding-Tools effektiv einsetzen, Context Engineering anwenden und Ihre Entwicklungsproduktivität signifikant steigern
            </InfoBox>
          }
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // BLOCK 1: KI-FUNDAMENTALS
    // ============================================

    // SLIDE 3: Section Divider - Block 1
    {
      id: 3,
      component: () => (
        <StandardContentSlide
          title="Block 1"
          subtitle="KI-Fundamentals"
          background="primary"
        >
          <div className="flex flex-col items-center justify-center h-full space-y-12">
            <div className="text-8xl">🧠</div>
            <div className="text-center space-y-6">
              <h2 className="text-5xl font-bold text-white">KI-Fundamentals</h2>
              <p className="text-2xl text-white opacity-90">
                Von KI-Landschaft bis Prompt Engineering
              </p>
              <div className="flex gap-8 justify-center mt-8">
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">90 min</p>
                  <p className="text-sm text-white opacity-75">Dauer</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">3</p>
                  <p className="text-sm text-white opacity-75">Themen</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">2</p>
                  <p className="text-sm text-white opacity-75">Übungen</p>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'fade',
    },

    // SLIDE 4: KI-Landschaft - Theorie
    {
      id: 4,
      component: () => (
        <StandardGridSlide
          title="1.1 KI-Landschaft verstehen"
          subtitle="Von KI über ML zu Generative AI - Die Theorie"
          columns={2}
          background="light"
          items={[
            <FeatureCard
              icon={<IconSolid name="brain" size="2xl" className="text-primary-600" />}
              title="Künstliche Intelligenz (KI)"
              description="Der Oberbegriff"
              items={[
                'Simulation menschlicher Intelligenz durch Maschinen',
                'Lernfähige Systeme für komplexe Aufgaben',
                'Anwendungen: Spracherkennung, Bilderkennung, Entscheidungsfindung',
                'Erste Konzepte: 1950er Jahre (Alan Turing)',
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="project-diagram" size="2xl" className="text-secondary-600" />}
              title="Machine Learning (ML)"
              description="Lernende Algorithmen"
              items={[
                'Systeme lernen aus Daten ohne explizite Programmierung',
                'Supervised, Unsupervised, Reinforcement Learning',
                'Beispiele: Spam-Filter, Empfehlungssysteme, Vorhersagemodelle',
                'Durchbruch: 2000er Jahre mit Big Data',
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="network-wired" size="2xl" className="text-accent-600" />}
              title="Deep Learning (DL)"
              description="Neuronale Netze"
              items={[
                'Subset von ML mit mehrschichtigen neuronalen Netzen',
                'Erkennt komplexe Muster in großen Datenmengen',
                'Computer Vision, Sprachverarbeitung, Autonomes Fahren',
                'Revolution: 2012 mit ImageNet-Erfolg',
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="magic" size="2xl" className="text-purple-600" />}
              title="Generative AI"
              description="Die neueste Entwicklung"
              items={[
                'Erstellt neue Inhalte: Text, Code, Bilder, Musik',
                'Basis: Large Language Models (LLMs)',
                'ChatGPT, GitHub Copilot, DALL-E, Midjourney',
                'Mainstream: November 2022 mit ChatGPT',
              ]}
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // SLIDE 5: KI-Landschaft - Evolution Timeline
    {
      id: 5,
      component: () => (
        <StandardContentSlide
          title="Die Evolution der KI"
          subtitle="Von regelbasierten Systemen zu Generative AI"
          background="light"
        >
          <div className="space-y-8">
            {/* Timeline */}
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-primary-200"></div>
              
              <div className="space-y-6">
                {[
                  { year: '1950er', title: 'Symbolic AI', desc: 'Regelbasierte Systeme, Expertensysteme', icon: 'book' as const },
                  { year: '1990er', title: 'Machine Learning', desc: 'Statistische Modelle, Decision Trees', icon: 'chart-line' as const },
                  { year: '2010er', title: 'Deep Learning', desc: 'Neuronale Netze, Computer Vision', icon: 'brain' as const },
                  { year: '2020er', title: 'Generative AI', desc: 'LLMs, Multimodale Modelle', icon: 'magic' as const },
                ].map((item, idx) => (
                  <div key={idx} className={`flex items-center gap-6 ${idx % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                    <div className={`flex-1 ${idx % 2 === 0 ? 'text-right' : 'text-left'}`}>
                      <h4 className="text-xl font-bold text-primary-600">{item.year}</h4>
                      <h5 className="text-lg font-semibold">{item.title}</h5>
                      <p className="text-sm opacity-75">{item.desc}</p>
                    </div>
                    <div className="w-16 h-16 rounded-full bg-primary-600 flex items-center justify-center z-10">
                      <IconSolid name={item.icon} className="text-white" size="xl" />
                    </div>
                    <div className="flex-1"></div>
                  </div>
                ))}
              </div>
            </div>

            <InfoBox variant="info">
              <strong>Wichtig für Entwickler:</strong> Generative AI ist kein Ersatz für ML/DL, sondern eine Ergänzung. 
              Jede Technologie hat ihre Anwendungsfälle!
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 6: KI-Landschaft - Interaktive Diskussion
    {
      id: 6,
      component: () => (
        <StandardContentSlide
          title="💭 Interaktive Diskussion"
          subtitle="Wo nutzen SIE bereits KI?"
          background="secondary"
        >
          <div className="space-y-8">
            <InfoBox variant="tip">
              <h3 className="text-xl font-bold mb-3">Diskussionsfragen (10 Minuten)</h3>
              <div className="space-y-4">
                <div className="pl-4 border-l-4 border-blue-400">
                  <p className="font-semibold">1. Welche KI-Tools nutzen Sie bereits im Alltag?</p>
                  <p className="text-sm opacity-75">z.B. Sprachassistenten, Empfehlungssysteme, Autocomplete</p>
                </div>
                <div className="pl-4 border-l-4 border-green-400">
                  <p className="font-semibold">2. Wo sehen Sie KI in Ihren Projekten?</p>
                  <p className="text-sm opacity-75">z.B. Code-Completion, Testing, Dokumentation</p>
                </div>
                <div className="pl-4 border-l-4 border-purple-400">
                  <p className="font-semibold">3. Was sind Ihre größten Bedenken bei KI?</p>
                  <p className="text-sm opacity-75">z.B. Qualität, Security, Ethik, Jobs</p>
                </div>
              </div>
            </InfoBox>

            <div className="grid grid-cols-3 gap-4">
              <FeatureCard
                icon={<IconSolid name="users" />}
                title="Gruppenarbeit"
                description="2-3 Personen pro Gruppe"
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="clock" />}
                title="5 Minuten"
                description="Diskussion in der Gruppe"
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="comments" />}
                title="5 Minuten"
                description="Ergebnisse im Plenum"
                variant="glass"
              />
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 7: Praktische Übung - KI-Kategorisierung
    {
      id: 7,
      component: function PraktischeUebung1() {
        const [showPrompt, setShowPrompt] = React.useState(false)
        
        return (
          <>
            <StandardContentSlide
              title="✏️ Praktische Übung 1"
              subtitle="KI-Systeme kategorisieren"
              background="primary"
            >
              <div className="space-y-6">
                <div className="flex justify-between items-start gap-4">
                  <InfoBox variant="warning">
                    <h3 className="text-xl font-bold mb-3">📝 Aufgabe (10 Minuten)</h3>
                    <p className="mb-4">
                      Erstellen Sie in VS Code eine Markdown-Datei und kategorisieren Sie folgende Systeme:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-sm">
                      <li>Netflix Recommendations</li>
                      <li>GitHub Copilot</li>
                      <li>Tesla Autopilot</li>
                      <li>Google Translate</li>
                      <li>DALL-E 2</li>
                      <li>Spam Filter</li>
                    </ul>
                  </InfoBox>
                  
                  <button
                    onClick={() => setShowPrompt(true)}
                    className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg shadow-lg hover:scale-105 transition-all duration-200 flex items-center gap-2"
                  >
                    <IconSolid name="info-circle" className="w-5 h-5" />
                    Details anzeigen
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FeatureCard
                    title="Kategorien"
                    items={[
                      'Symbolic AI',
                      'Machine Learning',
                      'Deep Learning',
                      'Generative AI',
                    ]}
                    variant="glass"
                  />
                  <FeatureCard
                    title="Zu beantworten"
                    items={[
                      'Welche Kategorie?',
                      'Warum diese Einordnung?',
                      'Welche Daten genutzt?',
                    ]}
                    variant="glass"
                  />
                </div>
              </div>
            </StandardContentSlide>

            {showPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setShowPrompt(false)}
                title={entwicklerschulungPrompts.categorization.title}
                description={entwicklerschulungPrompts.categorization.description}
                prompt={entwicklerschulungPrompts.categorization.prompt}
                tags={[...entwicklerschulungPrompts.categorization.tags]}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // SLIDE 8: LLM Deep Dive - Theorie
    {
      id: 8,
      component: () => (
        <StandardContentSlide
          title="1.2 Large Language Models - Deep Dive"
          subtitle="Wie funktionieren GPT, Claude & Co?"
          background="light"
        >
          {/* Architecture Flow */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-2">
                <IconSolid name="file-alt" size="2xl" className="text-primary-600" />
              </div>
              <p className="text-sm font-medium">Input Text</p>
            </div>
            
            <IconSolid name="arrow-right" size="2xl" className="text-gray-400" />
            
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-secondary-100 dark:bg-secondary-900/30 flex items-center justify-center mb-2">
                <IconSolid name="cube" size="2xl" className="text-secondary-600" />
              </div>
              <p className="text-sm font-medium">Tokenization</p>
            </div>
            
            <IconSolid name="arrow-right" size="2xl" className="text-gray-400" />
            
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center mb-2">
                <IconSolid name="brain" size="2xl" className="text-accent-600" />
              </div>
              <p className="text-sm font-medium">Neural Network</p>
            </div>
            
            <IconSolid name="arrow-right" size="2xl" className="text-gray-400" />
            
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-2">
                <IconSolid name="check" size="2xl" className="text-green-600" />
              </div>
              <p className="text-sm font-medium">Output</p>
            </div>
          </div>

          <InfoBox variant="info" className="mb-6">
            <strong>Was macht LLMs besonders?</strong>
            <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
              <div>
                <strong className="text-primary-600 dark:text-primary-400">Kontext-Verständnis:</strong>
                <p className="opacity-90">Verstehen Bedeutung im Kontext, nicht nur Wörter</p>
              </div>
              <div>
                <strong className="text-primary-600 dark:text-primary-400">Zero-Shot Learning:</strong>
                <p className="opacity-90">Aufgaben ohne spezifisches Training lösen</p>
              </div>
              <div>
                <strong className="text-primary-600 dark:text-primary-400">Transfer Learning:</strong>
                <p className="opacity-90">Wissen auf neue Domänen übertragen</p>
              </div>
              <div>
                <strong className="text-primary-600 dark:text-primary-400">Multi-Task-Fähigkeit:</strong>
                <p className="opacity-90">Verschiedene Aufgaben mit einem Modell</p>
              </div>
            </div>
          </InfoBox>

          <div className="grid grid-cols-3 gap-4">
            <FeatureCard
              icon={<IconSolid name="layer-group" size="xl" />}
              title="Transformer-Architektur"
              description="Attention-Mechanismus für parallele Verarbeitung"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="puzzle-piece" size="xl" />}
              title="Token-Based"
              description="Text wird in kleine Einheiten (Tokens) zerlegt"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="brain" size="xl" />}
              title="Probabilistisch"
              description="Vorhersage des wahrscheinlichsten nächsten Tokens"
              variant="elevated"
            />
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 9: LLM - Technische Details
    {
      id: 9,
      component: () => (
        <StandardGridSlide
          title="LLM - Technische Details"
          subtitle="Parameter, Context Windows & Training"
          columns={2}
          background="light"
          items={[
            <FeatureCard
              icon={<IconSolid name="database" className="text-blue-600" />}
              title="Modell-Parameter"
              description="Größe bestimmt Fähigkeiten"
              items={[
                'GPT-3.5: 175 Milliarden Parameter',
                'GPT-4: ~1.7 Billionen Parameter (geschätzt)',
                'Claude 3 Opus: ~300-500 Mrd. Parameter',
                'Größer ≠ immer besser (Kosten, Speed)',
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="window-maximize" className="text-green-600" />}
              title="Context Windows"
              description='Wie viel kann das Modell "sehen"?'
              items={[
                'GPT-3.5: 4K-16K Tokens (~3K-12K Wörter)',
                'GPT-4: 8K-128K Tokens',
                'Claude 3: 200K Tokens (~150K Wörter)',
                'Gemini 1.5: 1M Tokens (ganze Codebases!)',
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="graduation-cap" className="text-purple-600" />}
              title="Training-Prozess"
              description="Wie LLMs lernen"
              items={[
                'Pre-Training: Milliarden Texte aus dem Internet',
                'Fine-Tuning: Spezifische Aufgaben & Daten',
                'RLHF: Reinforcement Learning from Human Feedback',
                'Kosten: Millionen bis Milliarden Dollar',
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="bolt" className="text-yellow-600" />}
              title="Inference vs Training"
              description="Unterschied verstehen"
              items={[
                'Training: Einmalig, sehr teuer, Monate',
                'Inference: Jede Anfrage, günstig, Sekunden',
                'Wir nutzen: Inference (API-Calls)',
                'Training: Nur für eigene Modelle relevant',
              ]}
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // SLIDE 10: LLM - Interaktive Demo
    {
      id: 10,
      component: () => (
        <StandardContentSlide
          title="💭 Live-Demo: LLM in Action"
          subtitle="Probieren wir es gemeinsam aus"
          background="secondary"
        >
          <div className="space-y-4">
            <InfoBox variant="tip">
              <h3 className="text-lg font-bold mb-2">Live-Demonstration (15 Minuten)</h3>
              <p className="text-sm">Wir testen verschiedene Szenarien mit ChatGPT/Claude</p>
            </InfoBox>

            <div className="grid grid-cols-2 gap-3">
              <FeatureCard
                icon={<IconSolid name="code" />}
                title="1. Code-Generierung"
                items={[
                  'Funktionen erstellen',
                  'Algorithmen implementieren',
                ]}
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="search" />}
                title="2. Code-Erklärung"
                items={[
                  'Legacy Code analysieren',
                  'Regex-Pattern erklären',
                ]}
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="bug" />}
                title="3. Debugging"
                items={[
                  'Stack Trace analysieren',
                  'Logic Bugs finden',
                ]}
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="magic" />}
                title="4. Refactoring"
                items={[
                  'Clean Code anwenden',
                  'Performance optimieren',
                ]}
                variant="glass"
              />
            </div>

            <div className="bg-gray-900 p-3 rounded-lg">
              <p className="text-green-400 font-mono text-xs mb-1">Beispiel-Prompts:</p>
              <pre className="text-white text-xs">
{`"Erkläre diese Regex: ^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}$"
"TypeScript-Funktion für nested sorting eines Object-Arrays"
"Analysiere diesen Code und finde Bugs: [Code einfügen]"`}
              </pre>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 11: LLM - Praktische Übung
    {
      id: 11,
      component: function PraktischeUebung2() {
        const [showPrompt, setShowPrompt] = React.useState(false)
        
        return (
          <>
            <StandardContentSlide
              title="✏️ Praktische Übung 2"
              subtitle="LLM für Code-Aufgaben nutzen"
              background="primary"
            >
              <div className="space-y-4">
                <InfoBox variant="warning">
                  <h3 className="text-lg font-bold mb-2">📝 Aufgabe (15 Minuten)</h3>
                  <p className="text-sm">
                    Nutzen Sie ChatGPT oder Claude für diese Coding-Challenges:
                  </p>
                </InfoBox>

                <div className="grid grid-cols-3 gap-3">
                  <FeatureCard
                    title="Challenge 1"
                    description="⭐ Einsteiger"
                    items={[
                      'Array von User-Objekten',
                      'Gruppiere nach Role',
                      'Sortiere nach Name',
                    ]}
                    variant="glass"
                  />
                  <FeatureCard
                    title="Challenge 2"
                    description="⭐⭐ Fortgeschritten"
                    items={[
                      'TypeScript HTTP-Client',
                      'Error Handling',
                      'Retry-Logic',
                    ]}
                    variant="glass"
                  />
                  <FeatureCard
                    title="Challenge 3"
                    description="⭐⭐⭐ Expert"
                    items={[
                      'LRU Cache O(1)',
                      'TypeScript Generics',
                      'Unit Tests',
                    ]}
                    variant="glass"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <p className="text-sm text-green-400 mb-1">Bewertungskriterien:</p>
                    <ul className="text-xs text-white space-y-1">
                      <li>✓ Funktioniert der Code?</li>
                      <li>✓ Ist er gut lesbar?</li>
                      <li>✓ Edge Cases abgedeckt?</li>
                    </ul>
                  </div>
                  <button
                    onClick={() => setShowPrompt(true)}
                    className="bg-blue-900/30 p-3 rounded-lg border border-blue-400 hover:bg-blue-900/50 transition-colors cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <IconSolid name="info-circle" className="text-blue-400" />
                      <p className="text-xs font-semibold text-blue-300">Details anzeigen</p>
                    </div>
                    <p className="text-xs text-white/80">
                      Vollständige Aufgabenstellung mit Beispiel-Prompts
                    </p>
                  </button>
                </div>
              </div>
            </StandardContentSlide>

            {showPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setShowPrompt(false)}
                title={entwicklerschulungPrompts.llmCodeTasks.title}
                description={entwicklerschulungPrompts.llmCodeTasks.description}
                prompt={entwicklerschulungPrompts.llmCodeTasks.prompt}
                tags={[...entwicklerschulungPrompts.llmCodeTasks.tags]}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // SLIDE 12: Prompt Engineering - Theorie
    {
      id: 12,
      component: () => (
        <StandardGridSlide
          title="1.3 Prompt Engineering Essentials"
          subtitle="Vom schlechten zum perfekten Prompt"
          columns={2}
          background="primary"
          items={[
            <FeatureCard
              icon={<IconSolid name="times-circle" size="2xl" className="text-red-400" />}
              title="❌ Schlechte Prompts"
              description="Vage, mehrdeutig, ohne Kontext"
              items={[
                '"Schreib mir eine Funktion"',
                '"Mach das besser"',
                '"Erstelle eine API"',
                '"Fix den Bug"',
              ]}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="check-circle" size="2xl" className="text-green-400" />}
              title="✅ Gute Prompts"
              description="Spezifisch, strukturiert, mit Kontext"
              items={[
                'Klare Aufgabenbeschreibung',
                'Relevanter Kontext (Sprache, Framework)',
                'Gewünschtes Format/Stil',
                'Constraints und Requirements',
                'Beispiele (wenn nötig)',
              ]}
              variant="glass"
            />,
          ]}
          footer={
            <InfoBox variant="tip">
              <strong>Golden Rule:</strong> Je präziser der Prompt, desto besser das Ergebnis. Investiere Zeit in gute Prompts!
            </InfoBox>
          }
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 13: Advanced Prompt Techniques
    // ============================================
    {
      id: 13,
      component: () => (
        <StandardContentSlide
          title="Fortgeschrittene Prompt-Techniken"
          subtitle="Von Zero-Shot bis Chain-of-Thought"
          background="light"
        >
          <div className="grid grid-cols-2 gap-6 mb-6">
            <FeatureCard
              title="1. Zero-Shot"
              description="Direkte Aufgabe ohne Beispiele"
              items={[
                'Einfachste Form',
                'Für klare, standardisierte Aufgaben',
                'Beispiel: "Erstelle eine TypeScript-Klasse für User"',
              ]}
            />
            <FeatureCard
              title="2. Few-Shot"
              description="Aufgabe mit 1-3 Beispielen"
              items={[
                'Zeige gewünschtes Format/Stil',
                'Für konsistente Ergebnisse',
                'Beispiel: "Wie diese 2 Funktionen, erstelle..."',
              ]}
            />
            <FeatureCard
              title="3. Chain-of-Thought"
              description="Schrittweise Problemlösung"
              items={[
                'Zerlege komplexe Aufgaben',
                'Denke laut" mit dem LLM',
                'Beispiel: "Erkläre jeden Schritt beim Refactoring"',
              ]}
            />
            <FeatureCard
              title="4. Role-Based"
              description="Definiere eine Rolle"
              items={[
                'LLM nimmt Expertenperspektive ein',
                'Für spezialisierte Aufgaben',
                'Beispiel: "Als Senior-Architekt, bewerte..."',
              ]}
            />
          </div>

          <InfoBox variant="tip">
            <strong>Meta-Prompting:</strong> Lass das LLM den perfekten Prompt für deine Aufgabe generieren!
            <p className="mt-2 text-sm opacity-90">
              Beispiel: "Erstelle einen optimalen Prompt für: [deine Aufgabe]"
            </p>
          </InfoBox>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 14: Prompt Engineering - Interaktive Übung
    {
      id: 14,
      component: function InteraktiveUebungPromptVerbesserung() {
        const [showPrompt, setShowPrompt] = React.useState(false)
        
        return (
          <>
            <StandardContentSlide
              title="💭 Interaktive Übung: Prompt Verbesserung"
              subtitle="Vom schlechten zum guten Prompt"
              background="secondary"
            >
              <div className="space-y-6">
                <InfoBox variant="warning">
                  <h3 className="text-xl font-bold mb-3">Gruppenarbeit (15 Minuten)</h3>
                  <p className="mb-4">Verbessern Sie diese schlechten Prompts gemeinsam:</p>
                </InfoBox>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-red-900/30 p-4 rounded-lg border border-red-400">
                      <p className="text-sm font-bold text-red-300 mb-2">❌ Schlechter Prompt #1</p>
                      <code className="text-sm">"Erstelle eine Funktion zum Sortieren"</code>
                    </div>
                    <div className="bg-red-900/30 p-4 rounded-lg border border-red-400">
                      <p className="text-sm font-bold text-red-300 mb-2">❌ Schlechter Prompt #2</p>
                      <code className="text-sm">"Fix den Bug im Code"</code>
                    </div>
                    <div className="bg-red-900/30 p-4 rounded-lg border border-red-400">
                      <p className="text-sm font-bold text-red-300 mb-2">❌ Schlechter Prompt #3</p>
                      <code className="text-sm">"Schreibe Tests"</code>
                    </div>
                    <button
                      onClick={() => setShowPrompt(true)}
                      className="w-full bg-blue-600/30 p-3 rounded-lg border border-blue-400 hover:bg-blue-600/50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <IconSolid name="info-circle" className="text-blue-300" />
                        <p className="text-sm font-semibold text-blue-200">Detaillierte Lösung anzeigen</p>
                      </div>
                    </button>
                  </div>
                  
                  <FeatureCard
                    icon={<IconSolid name="lightbulb" className="text-yellow-400" />}
                    title="Verbesserungs-Checkliste"
                    items={[
                      '✓ Spezifische Aufgabe definiert?',
                      '✓ Programmiersprache genannt?',
                      '✓ Kontext gegeben?',
                      '✓ Gewünschtes Format beschrieben?',
                      '✓ Constraints/Requirements genannt?',
                      '✓ Beispiele hinzugefügt?',
                    ]}
                    variant="glass"
                  />
                </div>

                <div className="flex gap-4">
                  <FeatureCard
                    icon={<IconSolid name="users" />}
                    title="10 Min"
                    description="Gruppenarbeit"
                    variant="glass"
                  />
                  <FeatureCard
                    icon={<IconSolid name="comments" />}
                    title="5 Min"
                    description="Präsentation"
                    variant="glass"
                  />
                </div>
              </div>
            </StandardContentSlide>

            {showPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setShowPrompt(false)}
                title={entwicklerschulungPrompts.promptImprovement.title}
                description={entwicklerschulungPrompts.promptImprovement.description}
                prompt={entwicklerschulungPrompts.promptImprovement.prompt}
                tags={[...entwicklerschulungPrompts.promptImprovement.tags]}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // SLIDE 15: Prompt Engineering - Praktische Aufgabe
    {
      id: 15,
      component: function PraktischeUebung3() {
        const [showPrompt, setShowPrompt] = React.useState(false)
        
        return (
          <>
            <StandardContentSlide
              title="✏️ Praktische Übung 3"
              subtitle="Prompt-Engineering im echten Einsatz"
              background="primary"
            >
              <div className="space-y-6">
                <InfoBox variant="warning">
                  <h3 className="text-xl font-bold mb-3">📝 Aufgabe (20 Minuten)</h3>
                  <p className="mb-4">
                    Nutzen Sie ChatGPT/Claude mit optimierten Prompts für diese Szenarien:
                  </p>
                </InfoBox>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <FeatureCard
                      title="Szenario 1: Code-Review"
                      description="⭐ Einsteiger"
                      items={[
                        'Prompt für Code-Review mit Best Practices',
                        'Konkrete Verbesserungsvorschläge',
                      ]}
                      variant="glass"
                    />
                    <FeatureCard
                      title="Szenario 2: Refactoring"
                      description="⭐⭐ Fortgeschritten"
                      items={[
                        'Legacy Code modernisieren mit klaren Zielen',
                        'Step-by-step Anleitung anfordern',
                      ]}
                      variant="glass"
                    />
                  </div>
                  <div className="space-y-4">
                    <FeatureCard
                      title="Szenario 3: Architektur"
                      description="⭐⭐⭐ Expert"
                      items={[
                        'Mikroservice-Architektur mit Constraints',
                        'Inkl. Diagramm-Vorschlag',
                      ]}
                      variant="glass"
                    />
                    <button
                      onClick={() => setShowPrompt(true)}
                      className="w-full bg-gray-900 p-4 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer text-left border border-gray-700"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <IconSolid name="info-circle" className="text-blue-400" />
                        <p className="text-sm font-semibold text-green-400">Details & Beispiel-Prompts</p>
                      </div>
                      <ul className="text-xs text-white/80 space-y-1">
                        <li>✓ Nutze Few-Shot mit Beispielen</li>
                        <li>✓ Definiere die Rolle des LLM</li>
                        <li>✓ Gib explizites Format vor</li>
                        <li>✓ Iteriere basierend auf Output</li>
                      </ul>
                    </button>
                  </div>
                </div>
              </div>
            </StandardContentSlide>

            {showPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setShowPrompt(false)}
                title={entwicklerschulungPrompts.promptImprovement.title}
                description={entwicklerschulungPrompts.promptImprovement.description}
                prompt={entwicklerschulungPrompts.promptImprovement.prompt}
                tags={[...entwicklerschulungPrompts.promptImprovement.tags]}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // ============================================
    // BLOCK 2: AI-CODING-TOOLS
    // ============================================

    // SLIDE 16: Section Divider - Block 2
    {
      id: 16,
      component: () => (
        <StandardContentSlide
          title="Block 2"
          subtitle="AI-Coding-Tools"
          background="secondary"
        >
          <div className="flex flex-col items-center justify-center h-full space-y-12">
            <div className="text-8xl">🛠️</div>
            <div className="text-center space-y-6">
              <h2 className="text-5xl font-bold text-white">AI-Coding-Tools</h2>
              <p className="text-2xl text-white opacity-90">
                Marktübersicht und Tool-Vergleich
              </p>
              <div className="flex gap-8 justify-center mt-8">
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">60 min</p>
                  <p className="text-sm text-white opacity-75">Dauer</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">6</p>
                  <p className="text-sm text-white opacity-75">Tools</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">1</p>
                  <p className="text-sm text-white opacity-75">Demo</p>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'fade',
    },

    // SLIDE 17: AI Tools - Theory (Enhanced)
    {
      id: 17,
      component: () => (
        <StandardGridSlide
          title="AI-Coding-Tools - Übersicht"
          subtitle="Die wichtigsten Tools im Vergleich"
          columns={3}
          background="light"
          items={[
            <FeatureCard
              icon={<IconSolid name="github" className="text-gray-900" />}
              title="GitHub Copilot"
              description="Der Marktführer"
              items={['VS Code Integration', 'Code Completion', 'Chat-Interface', 'Enterprise Support']}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="edit" className="text-blue-600" />}
              title="Cursor"
              description="Der Innovator"
              items={['Native AI-IDE', 'Composer Mode', 'Codebase-wide', 'Multi-file Edits']}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="robot" className="text-orange-600" />}
              title="Claude (Anthropic)"
              description="Der Architekt"
              items={['Artifacts', '200K Context', 'Code Analysis', 'Reasoning']}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="terminal" className="text-green-600" />}
              title="Cline"
              description="Der Autonome"
              items={['VS Code Extension', 'Autonomous Coding', 'Terminal Access', 'File Operations']}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="wind" className="text-cyan-600" />}
              title="Windsurf"
              description="Der Aufsteiger"
              items={['Flow Mode', 'Multi-file Edits', 'Codeium-backed', 'Fast Performance']}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="building" className="text-purple-600" />}
              title="Enterprise Options"
              description="Für Unternehmen"
              items={['Amazon CodeWhisperer', 'Tabnine', 'Codeium', 'On-Premise Lösungen']}
              variant="glass"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // SLIDE 18: Tool Comparison - Detailed
    {
      id: 18,
      component: () => (
        <StandardContentSlide
          title="Tool-Vergleich: Top 4 im Detail"
          subtitle="Feature-Matrix und Einsatzszenarien"
          background="light"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div className="font-bold bg-gray-800 text-white p-3 rounded">Feature</div>
              <div className="font-bold bg-gray-700 text-white p-3 rounded text-center">Copilot</div>
              <div className="font-bold bg-blue-700 text-white p-3 rounded text-center">Cursor</div>
              <div className="font-bold bg-orange-700 text-white p-3 rounded text-center">Claude</div>
              
              <div className="bg-gray-100 text-gray-900 p-3 rounded font-medium">Code Completion</div>
              <div className="bg-green-100 text-green-800 p-3 rounded text-center font-bold">+++</div>
              <div className="bg-green-100 text-green-800 p-3 rounded text-center font-bold">+++</div>
              <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center font-bold">++</div>
              
              <div className="bg-gray-100 text-gray-900 p-3 rounded font-medium">Kontext-Verständnis</div>
              <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center font-bold">++</div>
              <div className="bg-green-100 text-green-800 p-3 rounded text-center font-bold">+++</div>
              <div className="bg-green-100 text-green-800 p-3 rounded text-center font-bold">+++</div>
              
              <div className="bg-gray-100 text-gray-900 p-3 rounded font-medium">Multi-File Edits</div>
              <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center font-bold">+</div>
              <div className="bg-green-100 text-green-800 p-3 rounded text-center font-bold">+++</div>
              <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center font-bold">++</div>
              
              <div className="bg-gray-100 text-gray-900 p-3 rounded font-medium">IDE Integration</div>
              <div className="bg-green-100 text-green-800 p-3 rounded text-center font-bold">+++</div>
              <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center font-bold">++</div>
              <div className="bg-red-100 text-red-800 p-3 rounded text-center font-bold">+</div>
              
              <div className="bg-gray-100 text-gray-900 p-3 rounded font-medium">Preis/Monat</div>
              <div className="bg-blue-100 text-blue-800 p-3 rounded text-center font-semibold">$10-39</div>
              <div className="bg-blue-100 text-blue-800 p-3 rounded text-center font-semibold">$20-200</div>
              <div className="bg-blue-100 text-blue-800 p-3 rounded text-center font-semibold">$20-200</div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InfoBox variant="success">
                <strong>🎯 Empfehlung Einsteiger:</strong> GitHub Copilot - Einfacher Start, gute Integration
              </InfoBox>
              <InfoBox variant="info">
                <strong>🚀 Empfehlung Fortgeschritten:</strong> Cursor - Beste Features für komplexe Projekte
              </InfoBox>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 19: Interactive - Tool Selection
    {
      id: 19,
      component: () => (
        <StandardContentSlide
          title="💭 Interaktive Diskussion: Tool-Auswahl"
          subtitle="Welches Tool für welches Szenario?"
          background="secondary"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-blue-900/40 p-4 rounded-lg border border-blue-400">
                  <h3 className="font-bold text-blue-300 mb-2">📝 Szenario 1: Startup</h3>
                  <p className="text-sm text-white">
                    Kleines Team (5 Dev), schnelle Iteration, begrenztes Budget
                  </p>
                  <p className="text-xs text-blue-200 mt-2">Welches Tool würden Sie wählen? Warum?</p>
                </div>

                <div className="bg-green-900/40 p-4 rounded-lg border border-green-400">
                  <h3 className="font-bold text-green-300 mb-2">🏢 Szenario 2: Enterprise</h3>
                  <p className="text-sm text-white">
                    100+ Entwickler, Compliance-Anforderungen, On-Premise bevorzugt
                  </p>
                  <p className="text-xs text-green-200 mt-2">Was sind die Kriterien?</p>
                </div>

                <div className="bg-purple-900/40 p-4 rounded-lg border border-purple-400">
                  <h3 className="font-bold text-purple-300 mb-2">🔬 Szenario 3: Legacy</h3>
                  <p className="text-sm text-white">
                    Großes Legacy-Projekt, Refactoring benötigt, komplexe Codebase
                  </p>
                  <p className="text-xs text-purple-200 mt-2">Welche Features sind wichtig?</p>
                </div>
              </div>

              <FeatureCard
                icon={<IconSolid name="clipboard-list" className="text-yellow-400" />}
                title="Diskussions-Leitfaden"
                items={[
                  '1. Budget & Kosten',
                  '2. Team-Größe & Skill-Level',
                  '3. Technologie-Stack',
                  '4. Sicherheitsanforderungen',
                  '5. Integration mit bestehendem Setup',
                  '6. Lernkurve & Onboarding',
                ]}
                variant="glass"
              />
            </div>

            <div className="flex gap-4">
              <FeatureCard
                icon={<IconSolid name="users" />}
                title="Gruppen"
                description="3-4 Personen"
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="clock" />}
                title="10 Min"
                description="Diskussion"
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="comments" />}
                title="5 Min"
                description="Präsentation"
                variant="glass"
              />
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 20: Practical Task - Tool Setup
    {
      id: 20,
      component: function PraktischeUebung4() {
        const [showPrompt, setShowPrompt] = React.useState(false)
        
        return (
          <>
            <StandardContentSlide
              title="✏️ Praktische Übung 4"
              subtitle="AI-Tool Installation & Erste Schritte"
              background="primary"
            >
              <div className="space-y-6">
                <InfoBox variant="warning">
                  <h3 className="text-xl font-bold mb-3">🔧 Aufgabe (30 Minuten)</h3>
                  <p>Installieren und testen Sie ein AI-Coding-Tool Ihrer Wahl</p>
                </InfoBox>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <FeatureCard
                      title="Phase 1: Installation (10 Min)"
                      items={[
                        'Tool auswählen & installieren',
                        'Account erstellen & konfigurieren',
                      ]}
                      variant="glass"
                    />

                    <FeatureCard
                      title="Phase 2: Basis-Test (10 Min)"
                      items={[
                        'Code-Completion ausprobieren',
                        'Chat-Interface & Shortcuts testen',
                      ]}
                      variant="glass"
                    />
                  </div>

                  <div className="space-y-4">
                    <FeatureCard
                      title="Phase 3: Praktischer Test (10 Min)"
                      items={[
                        'Eigenen Code refactoren',
                        'Tests generieren & dokumentieren',
                      ]}
                      variant="glass"
                    />

                    <button
                      onClick={() => setShowPrompt(true)}
                      className="w-full bg-gray-900 p-4 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer text-left border border-gray-700"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <IconSolid name="info-circle" className="text-blue-400" />
                        <p className="text-sm font-bold text-green-400">📋 Detaillierte Anleitung</p>
                      </div>
                      <div className="space-y-2 text-xs text-white/80">
                        <div className="bg-gray-800 p-2 rounded">
                          <code>// Generiere eine Funktion zum Validieren von E-Mail-Adressen</code>
                        </div>
                        <div className="bg-gray-800 p-2 rounded">
                          <code>// Refactore diese Klasse zu TypeScript Interfaces</code>
                        </div>
                        <div className="bg-gray-800 p-2 rounded">
                          <code>// Schreibe Unit Tests für diese API-Route</code>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </StandardContentSlide>

            {showPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setShowPrompt(false)}
                title={entwicklerschulungPrompts.toolSetup.title}
                description={entwicklerschulungPrompts.toolSetup.description}
                prompt={entwicklerschulungPrompts.toolSetup.prompt}
                tags={[...entwicklerschulungPrompts.toolSetup.tags]}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // ============================================
    // BLOCK 3: COPILOT DEEP DIVE
    // ============================================

    // SLIDE 21: Section Divider - Block 3
    {
      id: 21,
      component: () => (
        <StandardContentSlide
          title="Block 3"
          subtitle="Copilot Deep Dive"
          background="primary"
        >
          <div className="flex flex-col items-center justify-center h-full space-y-12">
            <div className="text-8xl">🤖</div>
            <div className="text-center space-y-6">
              <h2 className="text-5xl font-bold text-white">Copilot Deep Dive</h2>
              <p className="text-2xl text-white opacity-90">
                Hands-on Workshop mit echten Projekten
              </p>
              <div className="flex gap-8 justify-center mt-8">
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">90 min</p>
                  <p className="text-sm text-white opacity-75">Dauer</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">2</p>
                  <p className="text-sm text-white opacity-75">Workshops</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">Live</p>
                  <p className="text-sm text-white opacity-75">Coding</p>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'fade',
    },

    // SLIDE 22: Copilot Workflows - Enhanced Theory    // ============================================
    // SLIDE 22: Copilot Workflows - Enhanced Theory
    {
      id: 22,
      component: () => (
        <StandardContentSlide
          title="GitHub Copilot - Core Workflows"
          subtitle="3 essenzielle Arbeitsmuster für maximale Produktivität"
          background="light"
        >
          <div className="grid grid-cols-3 gap-6">
            <FeatureCard
              icon={<IconSolid name="bolt" className="text-blue-600" />}
              title="1. Code Completion"
              description="Inline-Vorschläge beim Tippen"
              items={[
                'Tab: Vorschlag akzeptieren',
                'Alt+]: Nächster Vorschlag',
                'Funktionen vervollständigen',
                'Boilerplate generieren',
              ]}
              variant="glass"
            />
            <FeatureCard
              icon={<IconSolid name="comments" className="text-green-600" />}
              title="2. Chat-basiert"
              description="Konversation im Editor"
              items={[
                'Ctrl+I: Chat öffnen',
                'Code erklären lassen',
                'Refactoring-Vorschläge',
                'Bug-Fixes generieren',
              ]}
              variant="glass"
            />
            <FeatureCard
              icon={<IconSolid name="vial" className="text-purple-600" />}
              title="3. Test-Driven"
              description="Tests first, dann Implementation"
              items={[
                'Test schreiben',
                'Implementation generieren',
                'Refactor mit Sicherheit',
                'Coverage erhöhen',
              ]}
              variant="glass"
            />
          </div>

          <div className="grid grid-cols-4 gap-4 mt-6">
            <FeatureCard
              icon={<IconSolid name="rocket" />}
              title="2-3x"
              description="Schneller entwickeln"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="brain" />}
              title="Fokus"
              description="Mehr Zeit für Architektur"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="layer-group" />}
              title="Konsistenz"
              description="Einheitlicher Code-Stil"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="check" />}
              title="Qualität"
              description="Weniger Bugs"
              variant="elevated"
            />
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 23: Copilot - Live Demo Ideas
    {
      id: 23,
      component: () => (
        <StandardContentSlide
          title="🎬 Live Demo Vorschläge"
          subtitle="Was Sie jetzt gleich ausprobieren können"
          background="secondary"
        >
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <FeatureCard
                title="Demo 1: API Route erstellen"
                description="⏱️ 5 Minuten"
                items={[
                  '1. Kommentar: "Create GET /api/users route"',
                  '2. Express.js Handler generieren lassen',
                  '3. Error Handling hinzufügen',
                  '4. Validation mit Zod',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="Demo 2: React Component"
                description="⏱️ 5 Minuten"
                items={[
                  '1. Kommentar: "UserCard component with props"',
                  '2. TypeScript Interface generieren',
                  '3. Component mit Tailwind stylen',
                  '4. Props destructuring',
                ]}
                variant="glass"
              />
            </div>
            <div className="space-y-4">
              <FeatureCard
                title="Demo 3: Test schreiben"
                description="⏱️ 5 Minuten"
                items={[
                  '1. Funktion markieren',
                  '2. Chat: "Write unit tests for this"',
                  '3. Edge cases hinzufügen lassen',
                  '4. Tests ausführen',
                ]}
                variant="glass"
              />
              <div className="bg-gray-900 p-4 rounded-lg">
                <p className="text-sm text-green-400 font-bold mb-2">💡 Pro-Tipps:</p>
                <ul className="text-xs text-white space-y-1">
                  <li>✓ Beschreibende Kommentare schreiben</li>
                  <li>✓ Mehrere Vorschläge anschauen (Alt+])</li>
                  <li>✓ Chat für komplexe Fragen nutzen</li>
                  <li>✓ Code immer reviewen und verstehen!</li>
                </ul>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 24: Workshop 1 - E-Commerce API - Part 1 (Setup)
    {
      id: 24,
      component: function Workshop1Part1() {
        const [showPrompt, setShowPrompt] = React.useState(false)
        
        return (
          <>
            <StandardContentSlide
              title="🛠️ Workshop 1: E-Commerce Product API"
              subtitle="Teil 1: Setup & Struktur (10 Min)"
              background="primary"
            >
              <div className="space-y-6">
                <InfoBox variant="warning">
                  <h3 className="text-xl font-bold mb-2">🎯 Ziel</h3>
                  <p>REST API für Produktverwaltung mit Express.js, TypeScript und Copilot erstellen</p>
                </InfoBox>

                <div className="grid grid-cols-2 gap-6">
                  <FeatureCard
                    title="Schritt 1: Projekt initialisieren"
                    items={[
                      'mkdir ecommerce-api && cd ecommerce-api',
                      'npm init -y',
                      'npm install express typescript @types/express',
                      'npm install -D ts-node nodemon',
                    ]}
                    variant="glass"
                  />
                  <FeatureCard
                    title="Schritt 2: TypeScript Setup"
                    items={[
                      'npx tsc --init',
                      'tsconfig.json anpassen',
                      'package.json scripts hinzufügen',
                      'src/ Ordner erstellen',
                    ]}
                    variant="glass"
                  />
                </div>

                <button
                  onClick={() => setShowPrompt(true)}
                  className="w-full bg-gray-900 p-4 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer text-left border border-green-600"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <IconSolid name="info-circle" className="text-green-400" />
                    <p className="text-sm font-bold text-green-400">💬 Vollständige Workshop-Anleitung</p>
                  </div>
                  <code className="text-xs text-white/80 block mb-1">
                    // Create a basic Express TypeScript server with CORS and JSON middleware
                  </code>
                  <code className="text-xs text-white/80 block">
                    // Add health check route at GET /health
                  </code>
                </button>
              </div>
            </StandardContentSlide>

            {showPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setShowPrompt(false)}
                title={entwicklerschulungPrompts.ecommerceApi.title}
                description={entwicklerschulungPrompts.ecommerceApi.description}
                prompt={entwicklerschulungPrompts.ecommerceApi.prompt}
                tags={[...entwicklerschulungPrompts.ecommerceApi.tags]}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // SLIDE 25: Workshop 1 - Part 2 (Models & Routes)
    {
      id: 25,
      component: function Workshop1Part2() {
        const [showPrompt, setShowPrompt] = React.useState(false)
        
        return (
          <>
            <StandardContentSlide
              title="🛠️ Workshop 1: E-Commerce Product API"
              subtitle="Teil 2: Models & Routes (15 Min)"
              background="primary"
            >
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <FeatureCard
                      title="Schritt 3: Product Model"
                      items={[
                        'TypeScript Interface definieren',
                        'id, name, price, description',
                        'In-Memory Array als DB',
                      ]}
                      variant="glass"
                    />
                    <div className="bg-gray-900 p-3 rounded-lg">
                      <p className="text-xs text-green-400 mb-2">💬 Prompt:</p>
                      <code className="text-xs text-white">
                        // Create Product interface with id (string), name, price (number), description, inStock (boolean)
                      </code>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <FeatureCard
                      title="Schritt 4: CRUD Routes"
                      items={[
                        'GET /api/products - alle',
                        'GET /api/products/:id - einer',
                        'POST /api/products - neu',
                        'PUT /api/products/:id - update',
                        'DELETE /api/products/:id - löschen',
                      ]}
                      variant="glass"
                    />
                    <button
                      onClick={() => setShowPrompt(true)}
                      className="w-full bg-gray-900 p-3 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer text-left border border-blue-600"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <IconSolid name="info-circle" className="text-blue-400" />
                        <p className="text-xs font-bold text-green-400">💬 Vollständige Prompts & Code</p>
                      </div>
                      <code className="text-xs text-white/80">
                        // Create Express router with CRUD operations for Product
                      </code>
                    </button>
                  </div>
                </div>

                <InfoBox variant="tip">
                  <strong>Tipp:</strong> Nutzen Sie Copilot Chat (Ctrl+I) um komplette Router-Dateien zu generieren!
                </InfoBox>
              </div>
            </StandardContentSlide>

            {showPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setShowPrompt(false)}
                title={entwicklerschulungPrompts.ecommerceApi.title}
                description={entwicklerschulungPrompts.ecommerceApi.description}
                prompt={entwicklerschulungPrompts.ecommerceApi.prompt}
                tags={[...entwicklerschulungPrompts.ecommerceApi.tags]}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // SLIDE 26: Workshop 1 - Part 3 (Validation & Testing)
    {
      id: 26,
      component: function Workshop1Part3() {
        const [showPrompt, setShowPrompt] = React.useState(false)
        
        return (
          <>
            <StandardContentSlide
              title="🛠️ Workshop 1: E-Commerce Product API"
              subtitle="Teil 3: Validation & Testing (15 Min)"
              background="primary"
            >
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <FeatureCard
                    title="Schritt 5: Validation mit Zod"
                    items={[
                      'npm install zod',
                      'Schema für Product definieren',
                      'Middleware für Validation',
                      'Error Handling',
                    ]}
                    variant="glass"
                  />

                  <FeatureCard
                    title="Schritt 6: Tests schreiben"
                    items={[
                      'npm install -D jest supertest @types/jest',
                      'Jest konfigurieren',
                      'Tests für alle Routes',
                      'npm test ausführen',
                    ]}
                    variant="glass"
                  />
                </div>

                <button
                  onClick={() => setShowPrompt(true)}
                  className="w-full bg-gray-900 border-2 border-green-600 p-4 rounded-lg hover:bg-gray-800 transition-all"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <IconSolid name="info-circle" className="text-green-400" />
                    <p className="text-green-400 font-semibold">💬 Vollständige Validation & Testing Prompts</p>
                  </div>
                  <div className="space-y-1 text-xs text-gray-300">
                    <p>• Zod Schema mit Constraints</p>
                    <p>• Integration Tests mit Supertest</p>
                  </div>
                </button>

                <InfoBox variant="success">
                  <strong>✅ Fertig!</strong> Sie haben eine vollständige REST API mit Copilot erstellt - TypeScript, Validation, Tests
                </InfoBox>
              </div>
            </StandardContentSlide>

            {showPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setShowPrompt(false)}
                title={entwicklerschulungPrompts.ecommerceApi.title}
                description={entwicklerschulungPrompts.ecommerceApi.description}
                prompt={entwicklerschulungPrompts.ecommerceApi.prompt}
                tags={[...entwicklerschulungPrompts.ecommerceApi.tags]}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // ============================================
    // BLOCK 4: ADVANCED PRACTICE & SECURITY
    // ============================================

    // SLIDE 27: Section Divider - Block 4
    {
      id: 27,
      component: () => (
        <StandardContentSlide
          title="Block 4"
          subtitle="Advanced Practice & Security"
          background="accent"
        >
          <div className="flex flex-col items-center justify-center h-full space-y-12">
            <div className="text-8xl">🔐</div>
            <div className="text-center space-y-6">
              <h2 className="text-5xl font-bold text-white">Advanced Practice & Security</h2>
              <p className="text-2xl text-white opacity-90">
                Legacy Code, Tests & Sicherheit
              </p>
              <div className="flex gap-8 justify-center mt-8">
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">60 min</p>
                  <p className="text-sm text-white opacity-75">Dauer</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">2</p>
                  <p className="text-sm text-white opacity-75">Workshops</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">Best Practices</p>
                  <p className="text-sm text-white opacity-75">Security</p>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'fade',
    },

    // SLIDE 28: Workshop 2 - Legacy Code - Part 1
    {
      id: 28,
      component: function Workshop2Part1() {
        const [showPrompt, setShowPrompt] = React.useState(false)
        
        return (
          <>
            <StandardContentSlide
              title="🛠️ Workshop 2: Legacy Code Modernization"
              subtitle="Teil 1: Callback Hell → Async/Await (15 Min)"
              background="light"
            >
              <div className="space-y-6">
                <InfoBox variant="warning">
                  <h3 className="text-xl font-bold mb-2">🎯 Ziel</h3>
                  <p>Alten JavaScript-Code mit Copilot modernisieren</p>
                </InfoBox>

                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-red-900/30 p-4 rounded-lg border border-red-400">
                    <p className="text-sm font-bold text-red-300 mb-2">❌ Vorher: Callback Hell</p>
                    <pre className="text-xs text-white overflow-x-auto"><code>{`getUserData(userId, (err, user) => {
  if (err) return handleError(err);
  getOrders(user.id, (err, orders) => {
    if (err) return handleError(err);
    getProducts(orders, (err, products) => {
      if (err) return handleError(err);
      render(products);
    });
  });
});`}</code></pre>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-green-900/30 p-4 rounded-lg border border-green-400">
                      <p className="text-sm font-bold text-green-300 mb-2">✅ Nachher: Async/Await</p>
                      <pre className="text-xs text-white overflow-x-auto"><code>{`try {
  const user = await getUserData(userId);
  const orders = await getOrders(user.id);
  const products = await getProducts(orders);
  render(products);
} catch (error) {
  handleError(error);
}`}</code></pre>
                    </div>

                    <button
                      onClick={() => setShowPrompt(true)}
                      className="w-full bg-gray-900 border-2 border-amber-600 p-3 rounded-lg hover:bg-gray-800 transition-all"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <IconSolid name="info-circle" className="text-amber-400" />
                        <p className="text-amber-400 font-semibold text-sm">🔧 Vollständiger Modernisierungs-Guide</p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </StandardContentSlide>

            {showPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setShowPrompt(false)}
                title={entwicklerschulungPrompts.legacyModernization.title}
                description={entwicklerschulungPrompts.legacyModernization.description}
                prompt={entwicklerschulungPrompts.legacyModernization.prompt}
                tags={[...entwicklerschulungPrompts.legacyModernization.tags]}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // SLIDE 29: Workshop 2 - Part 2 (ES6+ Modernization)
    {
      id: 29,
      component: () => (
        <StandardContentSlide
          title="🛠️ Workshop 2: Legacy Code Modernization"
          subtitle="Teil 2: Moderne ES6+ Features (15 Min)"
          background="light"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FeatureCard
                title="var → const/let"
                items={[
                  'Block scope statt function scope',
                  'Immutability wo möglich',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="function → Arrow Functions"
                items={[
                  'Lexical this binding',
                  'Kürzere Syntax',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="Destructuring"
                items={[
                  'Object & Array destructuring',
                  'Default values',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="Spread & Rest"
                items={[
                  'Spread Operator (...)',
                  'Object/Array Merging',
                ]}
                variant="glass"
              />
            </div>

            <div className="bg-gray-900 p-3 rounded-lg">
              <p className="text-sm text-green-400 mb-1">💬 Mega-Prompt für Modernisierung:</p>
              <code className="text-xs text-white block">
                Modernize this code: ES6+, const/let, arrow functions, destructuring, async/await, TypeScript types
              </code>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 30: Workshop 3 - Test Coverage
    {
      id: 30,
      component: function Workshop3() {
        const [showPrompt, setShowPrompt] = React.useState(false)
        
        return (
          <>
            <StandardContentSlide
              title="🛠️ Workshop 3: Test Coverage erhöhen"
              subtitle="Automatische Test-Generierung mit Copilot (20 Min)"
              background="primary"
            >
              <div className="space-y-6">
                <InfoBox variant="warning">
                  <h3 className="text-xl font-bold mb-2">🎯 Ziel</h3>
                  <p>Von 0% auf 80%+ Test Coverage mit Copilot</p>
                </InfoBox>

                <div className="grid grid-cols-3 gap-4">
                  <FeatureCard
                    title="Phase 1: Unit Tests"
                    description="⏱️ 8 Min"
                    items={[
                      'Funktion markieren',
                      'Chat: "Write unit tests"',
                      'Edge Cases hinzufügen',
                      'Jest/Vitest Setup',
                    ]}
                    variant="glass"
                  />
                  <FeatureCard
                    title="Phase 2: Integration"
                    description="⏱️ 7 Min"
                    items={[
                      'API-Routes testen',
                      'Database Mocks',
                      'Supertest für HTTP',
                      'Happy & Error Path',
                    ]}
                    variant="glass"
                  />
                  <FeatureCard
                    title="Phase 3: Coverage"
                    description="⏱️ 5 Min"
                    items={[
                      'npm test -- --coverage',
                      'Lücken identifizieren',
                      'Fehlende Tests generieren',
                      'Threshold konfigurieren',
                    ]}
                    variant="glass"
                  />
                </div>

                <button
                  onClick={() => setShowPrompt(true)}
                  className="w-full bg-gray-900 border-2 border-purple-600 p-4 rounded-lg hover:bg-gray-800 transition-all"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <IconSolid name="info-circle" className="text-purple-400" />
                    <p className="text-purple-400 font-semibold">🧪 Vollständiger Test-Generierungs-Guide</p>
                  </div>
                  <div className="space-y-1 text-xs text-gray-300">
                    <p>• Unit Tests mit Edge Cases</p>
                    <p>• Integration Tests mit Mocks</p>
                    <p>• Coverage-Optimierung</p>
                  </div>
                </button>
              </div>
            </StandardContentSlide>

            {showPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setShowPrompt(false)}
                title={entwicklerschulungPrompts.testCoverage.title}
                description={entwicklerschulungPrompts.testCoverage.description}
                prompt={entwicklerschulungPrompts.testCoverage.prompt}
                tags={[...entwicklerschulungPrompts.testCoverage.tags]}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // ============================================
    // BLOCK 5: CONTEXT ENGINEERING (Slides 31-36)
    // ============================================

    // SLIDE 31: Section Divider - Context Engineering
    {
      id: 31,
      component: () => (
        <BlockDivider
          number={5}
          emoji="🎯"
          title="Context Engineering"
          subtitle="Systematische KI-Entwicklung"
          stats="⏱️ 45 Min | 📋 Framework & Praxis | 🚀 Enterprise-Ready"
        />
      ),
      transition: 'fade',
    },

    // SLIDE 32: Was ist Context Engineering?
    {
      id: 32,
      component: () => (
        <StandardContentSlide
          title="Was ist Context Engineering?"
          subtitle="Von einfachen Prompts zur systematischen KI-Entwicklung"
          background="light"
        >
          <div className="grid grid-cols-2 gap-6">
            <FeatureCard
              icon={<IconSolid name="lightbulb" className="text-yellow-600" />}
              title="Einfaches Prompting"
              description="Ad-hoc Anfragen"
              items={[
                '❌ Vage Formulierungen',
                '❌ Inkonsistente Ergebnisse',
                '❌ Viel Trial & Error',
                '❌ Schwer wiederholbar',
              ]}
            />
            <FeatureCard
              icon={<IconSolid name="rocket" className="text-green-600" />}
              title="Context Engineering"
              description="Systematischer Ansatz"
              items={[
                '✅ Strukturierte Kommunikation',
                '✅ Konsistente Qualität',
                '✅ Wiederholbare Prozesse',
                '✅ Enterprise-tauglich',
              ]}
            />
          </div>

          <InfoBox variant="tip">
            <strong>💡 Definition:</strong> Context Engineering ist die systematische Methodik zur Strukturierung von Informationen und Anforderungen für optimale KI-Interaktion in professionellen Entwicklungsprojekten.
          </InfoBox>

          <div className="grid grid-cols-4 gap-4 mt-6">
            <FeatureCard
              icon={<IconSolid name="bullseye" className="text-primary-600" />}
              title="Klarheit"
              description="Präzise Ziele"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="sitemap" className="text-secondary-600" />}
              title="Struktur"
              description="Systematischer Aufbau"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="database" className="text-accent-600" />}
              title="Kontext"
              description="Relevante Infos"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="sync-alt" className="text-green-600" />}
              title="Iteration"
              description="Kontinuierlich"
              variant="elevated"
            />
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 33: Das Context Engineering Framework
    {
      id: 33,
      component: () => (
        <StandardContentSlide
          title="Das Context Engineering Framework"
          subtitle="4-Schritt-Prozess für erfolgreiche KI-Interaktion"
          background="primary"
        >
          <div className="bg-gray-900 text-cyan-400 p-6 rounded-lg font-mono text-sm mb-6">
            <div className="space-y-4">
              <div>
                <span className="text-green-400"># ROLLE</span><br/>
                <span className="text-white">Du bist ein Senior TypeScript-Entwickler mit 10+ Jahren Erfahrung.<br/>
                Spezialisierung: React, Node.js, Clean Code, Testing Best Practices</span>
              </div>
              <div>
                <span className="text-green-400"># KONTEXT</span><br/>
                <span className="text-white">Projekt: E-Commerce Produktverwaltung<br/>
                Stack: React 18, TypeScript, Tailwind CSS, Vite<br/>
                Anforderungen: Responsive, Accessibility (WCAG AA), Type-Safe</span>
              </div>
              <div>
                <span className="text-green-400"># ANFORDERUNGEN</span><br/>
                <span className="text-white">Feature: Produktkatalog mit Filter & Suche<br/>
                - Grid Layout (3 Spalten Desktop, 1 Spalte Mobile)<br/>
                - Search Bar mit Debounce (300ms)<br/>
                - Category Filter (Dropdown)<br/>
                - Loading States & Error Handling</span>
              </div>
              <div>
                <span className="text-green-400"># LIEFERBARE</span><br/>
                <span className="text-white">Output: ProductCatalog.tsx Component<br/>
                - TypeScript Interfaces für Props & Data<br/>
                - Responsive Design (Mobile-First)<br/>
                - Unit Tests mit Vitest<br/>
                - Inline Code-Kommentare</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FeatureCard
              icon={<IconSolid name="times-circle" className="text-red-400" />}
              title="❌ Ohne Framework"
              items={[
                '"Mach eine Produktliste"',
                'Vage, mehrdeutig',
                'Unvorhersagbare Ergebnisse',
              ]}
              variant="glass"
            />
            <FeatureCard
              icon={<IconSolid name="check-circle" className="text-green-400" />}
              title="✅ Mit Framework"
              items={[
                'Strukturiert & vollständig',
                'Klare Erfolgskriterien',
                'Erste Iteration meist perfekt',
              ]}
              variant="glass"
            />
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 34: Praktische Übung - Context Engineering
    {
      id: 34,
      component: function PraktischeUebung5() {
        const [showPrompt, setShowPrompt] = React.useState(false)
        
        return (
          <>
            <StandardContentSlide
              title="✏️ Praktische Übung: Context Engineering"
              subtitle="Wandeln Sie einen schlechten Prompt in einen professionellen um (15 Min)"
              background="light"
            >
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-red-50 border-2 border-red-600 p-6 rounded-lg">
                    <h3 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-2">
                      <IconSolid name="times-circle" className="text-red-600" />
                      ❌ Schlechter Prompt
                    </h3>
                    <div className="bg-red-100 p-4 rounded">
                      <code className="text-red-900 text-sm">
                        "Schreib ne API für User-Verwaltung mit Login und so"
                      </code>
                    </div>
                    <ul className="mt-4 text-sm text-red-900 space-y-1">
                      <li>• Keine Tech-Stack Angaben</li>
                      <li>• Vage Anforderungen</li>
                      <li>• Keine Sicherheitsstandards</li>
                      <li>• Keine Erfolgskriterien</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 border-2 border-green-600 p-6 rounded-lg">
                    <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center gap-2">
                      <IconSolid name="check-circle" className="text-green-600" />
                      ✅ Ihre Aufgabe
                    </h3>
                    <div className="text-green-900 space-y-3">
                      <p className="font-semibold">Nutzen Sie das Framework:</p>
                      <ol className="list-decimal ml-5 space-y-2 text-sm">
                        <li><strong>ROLLE:</strong> Definieren Sie Expertise</li>
                        <li><strong>KONTEXT:</strong> Tech-Stack & Business</li>
                        <li><strong>ANFORDERUNGEN:</strong> Funktional & nicht-funktional</li>
                        <li><strong>LIEFERBARE:</strong> Konkrete Outputs</li>
                      </ol>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center">
                  <ModalTriggerButton
                    onClick={() => setShowPrompt(true)}
                    icon="info-circle"
                    text="💬 Lösung & Beispiel-Prompts anzeigen"
                    theme="blue"
                  />
                </div>

                <InfoBox variant="tip">
                  <strong>💡 Tipp:</strong> Beginnen Sie mit dem Business-Wert, dann technische Details. Konkrete Metriken statt vager Beschreibungen!
                </InfoBox>
              </div>
            </StandardContentSlide>

            {showPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setShowPrompt(false)}
                title={entwicklerschulungPrompts.contextEngineering.title}
                description={entwicklerschulungPrompts.contextEngineering.description}
                prompt={entwicklerschulungPrompts.contextEngineering.prompt}
                tags={[...entwicklerschulungPrompts.contextEngineering.tags]}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // SLIDE 35: Best Practices & Anti-Patterns
    {
      id: 35,
      component: () => (
        <StandardGridSlide
          title="Best Practices & Anti-Patterns"
          subtitle="Häufige Fehler vermeiden, Qualität sicherstellen"
          columns={2}
          background="secondary"
          items={[
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-white mb-4">✅ Best Practices</h3>
              <FeatureCard
                icon={<IconSolid name="bullseye" className="text-green-400" />}
                title="Klare Zieldefinition"
                items={[
                  'Messbare Erfolgskriterien',
                  'Konkrete Use Cases',
                  'Akzeptanzkriterien definieren',
                ]}
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="shield-check" className="text-blue-400" />}
                title="Sicherheit von Anfang an"
                items={[
                  'DSGVO/Compliance einbeziehen',
                  'Security Requirements explizit',
                  'Keine Secrets in Prompts',
                ]}
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="sync-alt" className="text-purple-400" />}
                title="Iterativer Ansatz"
                items={[
                  'Klein starten, inkrementell erweitern',
                  'Feedback-Loops einbauen',
                  'Continuous Improvement',
                ]}
                variant="glass"
              />
            </div>,
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-white mb-4">❌ Anti-Patterns</h3>
              <FeatureCard
                icon={<IconSolid name="exclamation-triangle" className="text-red-400" />}
                title="Scope Creep"
                items={[
                  '❌ "Erstelle ein komplettes CRM..."',
                  '✅ Aufgaben in kleine Schritte unterteilen',
                  '✅ MVP-Ansatz verwenden',
                ]}
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="ban" className="text-orange-400" />}
                title="Fehlende Constraints"
                items={[
                  '❌ Keine technischen Grenzen',
                  '✅ Budget, Zeit, Tech-Stack definieren',
                  '✅ Performance-Ziele angeben',
                ]}
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="question-circle" className="text-yellow-400" />}
                title="Vage Anforderungen"
                items={[
                  '❌ "Mach es besser, schneller"',
                  '✅ Konkrete Metriken (z.B. <2s LCP)',
                  '✅ SMART-Kriterien anwenden',
                ]}
                variant="glass"
              />
            </div>,
          ]}
        />
      ),
      transition: 'slide',
    },

    // SLIDE 36: Context Engineering in der Praxis
    {
      id: 36,
      component: () => (
        <StandardContentSlide
          title="Context Engineering in der Praxis"
          subtitle="Wie Sie es in Ihrem Team etablieren"
          background="light"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <FeatureCard
                icon={<IconSolid name="users" className="text-primary-600" />}
                title="1. Team-Templates"
                description="Wiederverwendbare Prompt-Vorlagen"
                items={[
                  'Frontend-Entwicklung',
                  'Backend/API',
                  'Testing & QA',
                  'Code Review',
                ]}
                variant="elevated"
              />
              <FeatureCard
                icon={<IconSolid name="book" className="text-secondary-600" />}
                title="2. Dokumentation"
                description="Best Practices festhalten"
                items={[
                  'Prompt-Bibliothek aufbauen',
                  'Erfolgreiche Patterns teilen',
                  'Lessons Learned',
                  'Team-Guidelines',
                ]}
                variant="elevated"
              />
              <FeatureCard
                icon={<IconSolid name="chart-line" className="text-accent-600" />}
                title="3. Kontinuierlich verbessern"
                description="Metriken & Feedback"
                items={[
                  'Erfolgsrate messen',
                  'Iteration-Counts tracken',
                  'Qualität bewerten',
                  'Team-Feedback einholen',
                ]}
                variant="elevated"
              />
            </div>

            <InfoBox variant="success">
              <h4 className="font-bold mb-3">🎯 Quick Start für Ihr Team:</h4>
              <ol className="space-y-2 text-sm">
                <li>1. <strong>Erstellen Sie 3-5 Standard-Templates</strong> für Ihre häufigsten Aufgaben</li>
                <li>2. <strong>Definieren Sie Qualitätskriterien</strong> (was ist "gut genug"?)</li>
                <li>3. <strong>Etablieren Sie Review-Prozesse</strong> für AI-generierten Code</li>
                <li>4. <strong>Messen Sie den Impact</strong> (Zeit gespart, Qualität verbessert)</li>
                <li>5. <strong>Iterieren Sie basierend auf Feedback</strong> aus dem Team</li>
              </ol>
            </InfoBox>

            <div className="bg-gradient-to-r from-primary-500/20 to-secondary-500/20 border-2 border-primary-500/50 rounded-xl p-6">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <IconSolid name="rocket" className="text-primary-600" />
                Nächste Schritte
              </h3>
              <div className="grid grid-cols-2 gap-4 text-lg">
                <div className="flex items-start gap-3">
                  <IconSolid name="check" className="text-green-600 mt-1" />
                  <div>Nutzen Sie Context Engineering in Ihren nächsten Copilot-Sessions</div>
                </div>
                <div className="flex items-start gap-3">
                  <IconSolid name="check" className="text-green-600 mt-1" />
                  <div>Erstellen Sie Team-Templates für wiederkehrende Aufgaben</div>
                </div>
                <div className="flex items-start gap-3">
                  <IconSolid name="check" className="text-green-600 mt-1" />
                  <div>Teilen Sie erfolgreiche Prompts mit dem Team</div>
                </div>
                <div className="flex items-start gap-3">
                  <IconSolid name="check" className="text-green-600 mt-1" />
                  <div>Messen Sie die Verbesserung Ihrer Entwicklungsgeschwindigkeit</div>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // ABSCHLUSS (Slides 37-39)
    // ============================================

    // SLIDE 37: Security Best Practices
    {
      id: 37,
      component: () => (
        <StandardGridSlide
          title="Security & Best Practices"
          subtitle="AI-Tools sicher und effektiv einsetzen"
          columns={2}
          background="light"
          items={[
            <FeatureCard
              icon={<IconSolid name="shield-alt" className="text-red-600" />}
              title="🔒 Security Concerns"
              items={[
                '❌ Keine Secrets/API-Keys in Prompts',
                '❌ Keine sensiblen Daten teilen',
                '❌ Compliance-Anforderungen beachten',
                '✅ Enterprise Versionen nutzen',
                '✅ Code immer reviewen',
              ]}
              variant="elevated"
            />,
            <FeatureCard
              icon={<IconSolid name="check-circle" className="text-green-600" />}
              title="✅ Best Practices"
              items={[
                '✓ Code verstehen, nicht blind kopieren',
                '✓ Tests für AI-generierten Code',
                '✓ Security Scans durchführen',
                '✓ Lizenz-Compliance prüfen',
                '✓ Team-Guidelines etablieren',
              ]}
              variant="elevated"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // SLIDE 38: Summary
    {
      id: 38,
      component: () => (
        <StandardContentSlide
          title="📚 Zusammenfassung Tag 1"
          subtitle="Was Sie heute gelernt haben"
          background="primary"
        >
          <div className="grid grid-cols-2 gap-4">
            <FeatureCard
              icon={<IconSolid name="graduation-cap" className="text-blue-400" />}
              title="Block 1: KI-Fundamentals"
              items={[
                '✓ KI-Landschaft',
                '✓ LLM Deep Dive',
                '✓ Prompt Engineering',
              ]}
              variant="glass"
            />
            <FeatureCard
              icon={<IconSolid name="tools" className="text-green-400" />}
              title="Block 2: AI-Tools"
              items={[
                '✓ Tool-Vergleich',
                '✓ Praktische Tests',
                '✓ Tool installiert',
              ]}
              variant="glass"
            />
            <FeatureCard
              icon={<IconSolid name="code" className="text-purple-400" />}
              title="Block 3: Copilot"
              items={[
                '✓ E-Commerce API',
                '✓ Workflows',
                '✓ Best Practices',
              ]}
              variant="glass"
            />
            <FeatureCard
              icon={<IconSolid name="shield" className="text-red-400" />}
              title="Block 4: Advanced"
              items={[
                '✓ Legacy modernisiert',
                '✓ Test Coverage',
                '✓ Security',
              ]}
              variant="glass"
            />
          </div>

          <div className="mt-4">
            <FeatureCard
              icon={<IconSolid name="rocket" className="text-cyan-400" />}
              title="Block 5: Context Engineering"
              items={[
                '✓ Systematische Methodik',
                '✓ Framework angewendet',
                '✓ Enterprise-Ready',
              ]}
              variant="glass"
            />
          </div>

          <InfoBox variant="success">
            <strong>🎉 Glückwunsch!</strong> Sie haben die Grundlagen moderner KI-gestützter Entwicklung gemeistert - inklusive professionellem Context Engineering!
          </InfoBox>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 39: Q&A
    {
      id: 39,
      component: () => (
        <StandardContentSlide
          title="Fragen & Diskussion"
          background="primary"
        >
          <div className="flex flex-col items-center justify-center h-full space-y-12">
            <div className="text-8xl">💬</div>
            <h2 className="text-5xl font-bold text-white text-center">
              Offene Fragen?
            </h2>
            <div className="grid grid-cols-3 gap-6 w-full max-w-4xl">
              <FeatureCard
                icon={<IconSolid name="comments" />}
                title="Diskussion"
                description="Ihre Erfahrungen"
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="lightbulb" />}
                title="Tipps"
                description="Weitere Best Practices"
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="calendar" />}
                title="Tag 2"
                description="Morgen geht's weiter"
                variant="glass"
              />
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'fade',
    },
  ],
};

export default entwicklerschulungTag1;
